import { Global, ThemeProvider } from '@emotion/react';
import type { Interpolation } from '@emotion/styled';
import React, { useMemo } from 'react';

import type { Frontmatter } from '~/shared/contexts/frontmatter';
import { FrontmatterProvider } from '~/shared/contexts/frontmatter';
import { WatchingSectionProvider } from '~/shared/contexts/watchingSection';
import { useEasterEgg } from '~/shared/hooks/useEasterEgg';
import type { Skin } from '~/theme';
import { createTheme } from '~/theme';

export interface PageContext {
  frontmatter: Frontmatter;
}

export interface LayoutProps {
  children: React.ReactElement;
  location: Location;
  pageContext: PageContext;
}

const Layout: React.FC<LayoutProps> = ({ children, location, pageContext }) => {
  const theme = useTheme(pageContext.frontmatter.skin as Skin);
  useEasterEgg(['Join us 👉 https://careers.devsisters.com/']);

  return (
    <ThemeProvider theme={theme}>
      <FrontmatterProvider value={pageContext.frontmatter}>
        <WatchingSectionProvider location={location}>
          <Global styles={globalStyles} />
          {children}
        </WatchingSectionProvider>
      </FrontmatterProvider>
    </ThemeProvider>
  );
};

export default Layout;

const globalStyles: Interpolation<any> = {
  body: {
    margin: 0,
    wordBreak: 'break-word',
    'html[lang=ko] > body': {
      wordBreak: 'keep-all',
    },
  },
  '#___gatsby': {
    // See [WI-19]
    // ~구 버전 크로미엄(네이버, 삼성 인터넷 브라우저 해당)의 왠지 모를 버그 대응~
    // 크롬 버전 문제라기에는 그냥 화면 많이 좁혔을 때도 발생하네
    //
    // html 엘리먼트 건드리는 순간 body-scroll-lock 이 동작 안해서 gatsby wrapper 쪽으로 셀렉터 변경
    overflowX: 'hidden',
  },
};

type CreateLayout = (LayoutComponent: React.ComponentType) => React.FC<LayoutProps>;
export const createLayout: CreateLayout =
  (LayoutComponent) =>
  ({ children, location, pageContext }) => {
    if (!pageContext.frontmatter) {
      /**
       * frontmatter가 주입되지 않은 페이지는 frontmatter를 사용하는 layout이 적용되지 않는다.
       * (useLocaleRedirect 등이 작성되어있는 자체 페이지만 동작함)
       * ex) /, /cookierun/, /ko/ 등
       */
      return <>{children}</>;
    }
    return (
      <Layout location={location} pageContext={pageContext}>
        <LayoutComponent />
      </Layout>
    );
  };

function useTheme(skin: Skin) {
  return useMemo(() => createTheme({ skin }), [skin]);
}
